import { Link } from 'react-router-dom';

export const linkClass =
  'underline decoration-1 underline-offset-4 transition-colors duration-fast hover:bg-mark hover:text-on-mark focus-visible:bg-mark focus-visible:text-on-mark';

export function Container({ as: Tag = 'div', className = '', children }) {
  return <Tag className={`mx-auto w-full max-w-page px-gutter ${className}`}>{children}</Tag>;
}

export function Note({ as: Tag = 'p', className = '', children, ...rest }) {
  return (
    <Tag className={`font-mono text-note text-ink-2 ${className}`} {...rest}>
      {children}
    </Tag>
  );
}

export function Mark({ children, delay = 300 }) {
  return (
    <span className="mark" style={{ '--mark-delay': `${delay}ms` }}>
      {children}
    </span>
  );
}

export function ExternalLink({ href, className = linkClass, children, ...rest }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className} {...rest}>
      {children}
      <span aria-hidden="true"> ↗</span>
      <span className="sr-only"> (opens in a new tab)</span>
    </a>
  );
}

export function TextLink({ to, href, className = '', children }) {
  const cls = `${linkClass} ${className}`;
  if (to) {
    return (
      <Link to={to} className={cls}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} className={cls}>
      {children}
    </a>
  );
}

const buttonBase =
  'inline-flex min-h-11 items-center justify-center bg-ink px-5 py-2 font-display font-semibold text-paper transition-colors duration-fast hover:bg-paper hover:text-ink hover:ring-2 hover:ring-inset hover:ring-ink focus-visible:bg-paper focus-visible:text-ink focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ink disabled:cursor-not-allowed disabled:bg-paper-2 disabled:text-ink-2';

export function Button({ href, onClick, className = '', children, ...rest }) {
  if (href) {
    return (
      <a href={href} className={`${buttonBase} ${className}`} {...rest}>
        {children}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={`${buttonBase} ${className}`} {...rest}>
      {children}
    </button>
  );
}

// Quiet text button used for secondary controls (gallery previous/next).
export function GhostButton({ onClick, className = '', children, ...rest }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`min-h-11 px-2 font-display font-semibold underline decoration-1 underline-offset-4 transition-colors duration-fast hover:bg-mark hover:text-on-mark focus-visible:bg-mark focus-visible:text-on-mark ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

export function Section({ id, title, children }) {
  return (
    <section id={id} aria-labelledby={`${id}-title`} className="scroll-mt-4 py-16 md:py-24">
      <Container>
        <h2 id={`${id}-title`} className="mb-8 font-display text-h2 md:mb-12">
          {title}
        </h2>
        {children}
      </Container>
    </section>
  );
}

// A list row: main content (8 cols) + mono margin note (4 cols) on wide screens.
export function Row({ to, className = '', children }) {
  const cls = `grid grid-cols-1 gap-x-8 gap-y-3 border-t border-rule py-8 md:grid-cols-12 ${className}`;
  if (to) {
    return (
      <Link to={to} className={`group ${cls}`}>
        {children}
      </Link>
    );
  }
  return <article className={cls}>{children}</article>;
}
